// src/pages/FamilyTreePage.js
// Updated: 10/20/2025 08:25 PM ET
// - Cursor-centered wheel zoom (Ctrl/Cmd + wheel) with scroll compensation
// - Touch pinch-zoom anchored to pinch midpoint (keeps fingers stationary)
// - Keeps existing drag-to-pan, link mode, and modals
// - Minor perf tweaks (willChange)

import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/layout/Layout';
import { useFamily } from '../context/FamilyContext';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, doc, writeBatch } from 'firebase/firestore';
import toast from 'react-hot-toast';
import Spinner from '../components/common/Spinner';
import TreeNode from '../components/familytree/TreeNode';
import ManageMemberModal from '../components/dashboard/ManageMemberModal';
import AddRelativeModal from '../components/dashboard/AddRelativeModal';
import TreeActionMenu from '../components/familytree/TreeActionMenu';
import LinkConfirmationModal from '../components/familytree/LinkConfirmationModal';
import { useDraggableScroll } from '../hooks/useDraggableScroll';
import '../components/familytree/FamilyTree.css';
import { FaUserPlus, FaLink, FaTimes, FaSearchMinus, FaSearchPlus, FaExpand } from 'react-icons/fa';

const AUTO_LINK_SPOUSE_AS_OTHER_PARENT = false; // keep false for blended families
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 2.0;
const ZOOM_STEP = 0.1;

// Normalize Firestore id or DocumentReference -> string id
const getId = (val) => {
  if (!val) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val.id) return val.id; // DocumentReference
  return null;
};

const normGender = (g) => {
  const s = (g || '').toString().trim().toLowerCase();
  if (['male', 'm', 'man', 'boy', 'father', 'dad'].includes(s)) return 'male';
  if (['female', 'f', 'woman', 'girl', 'mother', 'mom', 'mum'].includes(s)) return 'female';
  return '';
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/** Convert a client point to container-local coordinates (relative to visible area). */
const getContainerPoint = (container, clientX, clientY) => {
  const rect = container.getBoundingClientRect();
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
};

/**
 * Set zoom while keeping the focal content point under (containerX, containerY)
 * stationary on screen by adjusting scrollLeft / scrollTop.
 */
const setZoomAt = (containerEl, prevZoom, nextZoom, containerX, containerY) => {
  if (!containerEl) return;
  const { scrollLeft, scrollTop } = containerEl;

  // "World" (unscaled) coordinates of the focal point before zoom
  const worldX = (scrollLeft + containerX) / prevZoom;
  const worldY = (scrollTop + containerY) / prevZoom;

  // New scroll so that world point maps back to same container position
  const newScrollLeft = worldX * nextZoom - containerX;
  const newScrollTop = worldY * nextZoom - containerY;

  containerEl.scrollLeft = newScrollLeft;
  containerEl.scrollTop = newScrollTop;
};

const FamilyTreePage = () => {
  const { familyData, isHost } = useFamily();

  // Raw members from Firestore (normalized IDs)
  const [members, setMembers] = useState([]);
  // Enriched nodes with computed `children` arrays (graph)
  const [graphNodes, setGraphNodes] = useState([]);
  // Root nodes to render (from enriched graph)
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modal, setModal] = useState({ type: null, data: null });
  const [menu, setMenu] = useState({ visible: false, x: 0, y: 0, member: null });

  const [isLinking, setIsLinking] = useState(false);
  const [sourcePerson, setSourcePerson] = useState(null);

  // Drag-to-scroll container ref (scrollable element)
  const draggableRef = useDraggableScroll();
  const treeContainerRef = useRef(null); // wrapper for absolute menu positioning

  // --- ZOOM STATE ---
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1); // keep an always-current zoom for gesture math
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  const pinchDataRef = useRef({
    pinchActive: false,
    startDistance: 0,
    startZoom: 1,
  });

  // --- Touch helpers ---
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const [t1, t2] = [touches[0], touches[1]];
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.hypot(dx, dy);
  };

  const getPinchCenter = (touches, containerEl) => {
    const x = (touches[0].clientX + touches[1].clientX) / 2;
    const y = (touches[0].clientY + touches[1].clientY) / 2;
    return getContainerPoint(containerEl, x, y);
  };

  // Mouse/Trackpad Zoom (Ctrl/Cmd + wheel) — centered at cursor
  const handleWheelZoom = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();

    const containerEl = draggableRef.current;
    if (!containerEl) return;

    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setZoom((prev) => {
      const next = clamp(prev + delta, ZOOM_MIN, ZOOM_MAX);
      if (next === prev) return prev;

      const { x, y } = getContainerPoint(containerEl, e.clientX, e.clientY);
      setZoomAt(containerEl, prev, next, x, y);
      return next;
    });
  };

  // Touch Pinch Zoom — anchored at pinch midpoint
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dist = getTouchDistance(e.touches) || 1;
      pinchDataRef.current.pinchActive = true;
      pinchDataRef.current.startDistance = dist;
      pinchDataRef.current.startZoom = zoomRef.current;
      // prevent browser default pinch-to-zoom/page scroll
      e.preventDefault();
    }
  };

  const onTouchMove = (e) => {
    const pd = pinchDataRef.current;
    if (!(pd.pinchActive && e.touches.length === 2)) return;

    const containerEl = draggableRef.current;
    if (!containerEl) return;

    const currentDist = getTouchDistance(e.touches) || 1;
    const scaleFactor = currentDist / (pd.startDistance || 1);
    const prev = zoomRef.current;
    const rawNext = pd.startZoom * scaleFactor;
    const next = clamp(rawNext, ZOOM_MIN, ZOOM_MAX);

    const center = getPinchCenter(e.touches, containerEl);
    setZoomAt(containerEl, prev, next, center.x, center.y);
    setZoom(next);

    e.preventDefault();
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) {
      pinchDataRef.current.pinchActive = false;
    }
  };

  // Optional: double-click to zoom in at point (desktop)
  const handleDoubleClick = (e) => {
    const containerEl = draggableRef.current;
    if (!containerEl) return;
    const prev = zoomRef.current;
    const next = clamp(prev + 0.2, ZOOM_MIN, ZOOM_MAX);
    const { x, y } = getContainerPoint(containerEl, e.clientX, e.clientY);
    setZoomAt(containerEl, prev, next, x, y);
    setZoom(next);
  };

  // --- READ & NORMALIZE ---
  useEffect(() => {
    if (!familyData?.id) return;
    setLoading(true);

    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('familyId', '==', familyData.id));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const memberList = snapshot.docs.map((d) => {
          const raw = { id: d.id, ...d.data() };
          // normalize relationship fields to string IDs
          return {
            ...raw,
            fatherId: getId(raw.fatherId),
            motherId: getId(raw.motherId),
            spouseId: getId(raw.spouseId),
          };
        });
        setMembers(memberList);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        toast.error('Failed to load family members.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [familyData?.id]);

  // --- BUILD ENRICHED GRAPH + ROOTS ---
  useEffect(() => {
    if (members.length === 0) {
      setGraphNodes([]);
      setTree([]);
      return;
    }

    // Create mutable copies with children arrays
    const membersMap = new Map(members.map((m) => [m.id, { ...m, children: [] }]));

    // Link children to existing parents in the map (de-dupe on both)
    for (const member of membersMap.values()) {
      const father = member.fatherId;
      const mother = member.motherId;

      if (father && membersMap.has(father)) {
        const f = membersMap.get(father);
        if (!f.children.some((child) => child.id === member.id)) f.children.push(member);
      }
      if (mother && membersMap.has(mother)) {
        const mNode = membersMap.get(mother);
        if (!mNode.children.some((child) => child.id === member.id)) mNode.children.push(member);
      }
    }

    // === Couple child union: ensure both spouses show the same children ===
    for (const node of membersMap.values()) {
      const sid = node.spouseId;
      if (!sid || !membersMap.has(sid)) continue;

      const spouse = membersMap.get(sid);
      // Deduplicate union by child.id
      const unionMap = new Map();
      for (const c of node.children) unionMap.set(c.id, c);
      for (const c of spouse.children) unionMap.set(c.id, c);

      const unionChildren = Array.from(unionMap.values());
      node.children = unionChildren;
      spouse.children = unionChildren; // keep both sides identical
    }

    const enriched = Array.from(membersMap.values());
    setGraphNodes(enriched); // store enriched nodes for spouse lookups

    // Determine roots: those with neither parent set
    const rootCandidates = enriched.filter((m) => !m.fatherId && !m.motherId);

    // One anchor per couple to avoid floaters/duplicates
    const processedCouples = new Set();
    const finalRoots = [];

    for (const candidate of rootCandidates) {
      const spouseId = candidate.spouseId;
      const coupleKey = spouseId ? [candidate.id, spouseId].sort().join('-') : candidate.id;

      if (processedCouples.has(coupleKey)) continue;

      if (spouseId && membersMap.has(spouseId)) {
        const spouse = membersMap.get(spouseId);
        const spouseIsAlsoRootCandidate = !spouse.fatherId && !spouse.motherId;

        if (!spouseIsAlsoRootCandidate) {
          processedCouples.add(coupleKey);
          continue;
        }

        // Anchor deterministically by lower ID if both are root candidates
        const [a] = [candidate.id, spouseId].sort();
        if (candidate.id !== a) {
          processedCouples.add(coupleKey);
          continue;
        }
      }

      finalRoots.push(candidate);
      processedCouples.add(coupleKey);
    }

    setTree(finalRoots);
  }, [members]);

  const closeMenu = () => setMenu((prev) => ({ ...prev, visible: false }));

  const handleNodeClick = (member, event) => {
    if (!isHost) return;
    event.stopPropagation();

    if (isLinking) {
      if (!sourcePerson) {
        setSourcePerson(member);
        toast('Now click the second person to complete the link.');
      } else {
        if (sourcePerson.id === member.id) return;
        setModal({ type: 'confirmLink', data: { source: sourcePerson, target: member } });
      }
      return;
    }

    const rect = treeContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenu({ visible: true, x: event.clientX - rect.left, y: event.clientY - rect.top, member });
  };

  // Linking with guards; writes string ids + ensures familyId
  const handleConfirmLink = async (source, target, relationship) => {
    const batch = writeBatch(db);
    try {
      if (relationship === 'spouse') {
        if (source.spouseId && source.spouseId !== target.id) {
          toast.error(`${source.firstName || 'Source'} already has a spouse linked.`);
          return;
        }
        if (target.spouseId && target.spouseId !== source.id) {
          toast.error(`${target.firstName || 'Target'} already has a spouse linked.`);
          return;
        }
        const sRef = doc(db, 'users', source.id);
        const tRef = doc(db, 'users', target.id);
        batch.update(sRef, { spouseId: target.id, familyId: familyData?.id || source.familyId });
        batch.update(tRef, { spouseId: source.id, familyId: familyData?.id || target.familyId });
      } else if (relationship === 'parent') {
        const g = normGender(source.gender);
        const cRef = doc(db, 'users', target.id);
        const ensureFamily = { familyId: familyData?.id || target.familyId };

        if (g === 'male') {
          if (target.fatherId && target.fatherId !== source.id) {
            toast.error(`${target.firstName || 'Child'} already has a father linked.`);
            return;
          }
          batch.update(cRef, { fatherId: source.id, ...ensureFamily });
          if (AUTO_LINK_SPOUSE_AS_OTHER_PARENT && source.spouseId && !target.motherId) {
            batch.update(cRef, { motherId: source.spouseId, ...ensureFamily });
          }
        } else if (g === 'female') {
          if (target.motherId && target.motherId !== source.id) {
            toast.error(`${target.firstName || 'Child'} already has a mother linked.`);
            return;
          }
          batch.update(cRef, { motherId: source.id, ...ensureFamily });
          if (AUTO_LINK_SPOUSE_AS_OTHER_PARENT && source.spouseId && !target.fatherId) {
            batch.update(cRef, { fatherId: source.spouseId, ...ensureFamily });
          }
        } else {
          toast.error('Please set the parent’s gender (Male/Female) before linking.');
          return;
        }
      }

      await batch.commit();
      toast.success('Relationship linked!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to link relationship.');
    } finally {
      setModal({ type: null, data: null });
      setIsLinking(false);
      setSourcePerson(null);
      closeMenu();
    }
  };

  const toggleLinkMode = () => {
    setIsLinking((prev) => !prev);
    setSourcePerson(null);
    closeMenu();
  };

  return (
    <Layout>
      {/* Modals */}
      {modal.type === 'edit' && (
        <ManageMemberModal
          familyId={familyData?.id}
          existingMember={modal.data}
          allMembers={members}
          onClose={() => setModal({ type: null, data: null })}
          onMemberAdded={() => {}}
        />
      )}

      {modal.type === 'addRelative' && (
        <AddRelativeModal
          familyId={familyData?.id}
          anchorMember={modal.data.anchor}
          relationshipType={modal.data.type}
          onClose={() => setModal({ type: null, data: null })}
        />
      )}

      {modal.type === 'addMember' && (
        <ManageMemberModal
          familyId={familyData?.id}
          existingMember={null}
          allMembers={members}
          onClose={() => setModal({ type: null, data: null })}
          onMemberAdded={() => {}}
        />
      )}

      {modal.type === 'confirmLink' && (
        <LinkConfirmationModal
          source={modal.data.source}
          target={modal.data.target}
          onConfirm={handleConfirmLink}
          onCancel={() => {
            setModal({ type: null, data: null });
            setIsLinking(false);
            setSourcePerson(null);
          }}
        />
      )}

      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-gray">Family Tree</h1>
          <p className="mt-2 text-gray-600">
            {isLinking
              ? 'Select the first person, then the second to create a link.'
              : 'Drag to pan. Ctrl/Cmd + Scroll to zoom. Pinch on touch devices.'}
          </p>
        </div>

        {isHost && (
          <button
            onClick={toggleLinkMode}
            className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium shadow w-full sm:w-auto justify-center ${
              isLinking ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
            }`}
          >
            {isLinking ? (
              <>
                <FaTimes /> Cancel Linking
              </>
            ) : (
              <>
                <FaLink /> Link Members
              </>
            )}
          </button>
        )}
      </div>

      {/* Zoom Controls */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() =>
            setZoom((z) => {
              const containerEl = draggableRef.current;
              if (!containerEl) return clamp(z - ZOOM_STEP, ZOOM_MIN, ZOOM_MAX);
              const rect = containerEl.getBoundingClientRect();
              const cx = rect.width / 2;
              const cy = rect.height / 2;
              const next = clamp(z - ZOOM_STEP, ZOOM_MIN, ZOOM_MAX);
              setZoomAt(containerEl, z, next, cx, cy);
              return next;
            })
          }
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 flex items-center gap-2"
          title="Zoom Out"
        >
          <FaSearchMinus /> <span className="hidden sm:inline">Zoom Out</span>
        </button>
        <button
          onClick={() => {
            const containerEl = draggableRef.current;
            if (containerEl) {
              // Reset centered to container middle
              const rect = containerEl.getBoundingClientRect();
              const cx = rect.width / 2;
              const cy = rect.height / 2;
              setZoomAt(containerEl, zoomRef.current, 1, cx, cy);
            }
            setZoom(1);
          }}
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 flex items-center gap-2"
          title="Reset Zoom"
        >
          <FaExpand /> <span className="hidden sm:inline">Reset</span>
        </button>
        <button
          onClick={() =>
            setZoom((z) => {
              const containerEl = draggableRef.current;
              if (!containerEl) return clamp(z + ZOOM_STEP, ZOOM_MIN, ZOOM_MAX);
              const rect = containerEl.getBoundingClientRect();
              const cx = rect.width / 2;
              const cy = rect.height / 2;
              const next = clamp(z + ZOOM_STEP, ZOOM_MIN, ZOOM_MAX);
              setZoomAt(containerEl, z, next, cx, cy);
              return next;
            })
          }
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 flex items-center gap-2"
          title="Zoom In"
        >
          <FaSearchPlus /> <span className="hidden sm:inline">Zoom In</span>
        </button>
        <span className="text-sm text-gray-500">Zoom: {(zoom * 100).toFixed(0)}%</span>
      </div>

      {/* Tree + menu */}
      <div ref={treeContainerRef} onClick={closeMenu} className="relative">
        <div
          ref={draggableRef}
          onWheel={handleWheelZoom}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onDoubleClick={handleDoubleClick}
          className={`tree-container bg-gray-100 rounded-lg overflow-auto relative ${
            isLinking ? 'cursor-crosshair' : 'cursor-grab'
          }`}
          style={{
            minHeight: '60vh',
            // Needed to allow custom pinch/drag gestures without browser interfering
            touchAction: 'none',
          }}
        >
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <Spinner size="lg" />
            </div>
          ) : tree.length > 0 ? (
            <div
              className="tree p-8 origin-top-left"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: '0 0',
                transition: 'transform 0.08s ease-out',
                willChange: 'transform',
              }}
            >
              <ul>
                {tree.map((rootNode) => (
                  <TreeNode
                    key={rootNode.id}
                    node={rootNode}
                    allNodes={graphNodes} // enriched nodes (with children)
                    isHost={isHost}
                    onNodeClick={handleNodeClick}
                    sourcePersonId={sourcePerson?.id}
                  />
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-center p-10">
              <p className="text-gray-500">Your family tree is empty.</p>
              {isHost && (
                <button
                  onClick={() => setModal({ type: 'addMember', data: null })}
                  className="mt-4 flex items-center gap-2 mx-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium shadow"
                >
                  <FaUserPlus /> Add a Starting Member
                </button>
              )}
            </div>
          )}
        </div>

        {menu.visible && (
          <div className="absolute z-50" style={{ top: menu.y, left: menu.x }}>
            <TreeActionMenu
              member={menu.member}
              onEdit={() => {
                setModal({ type: 'edit', data: menu.member });
                closeMenu();
              }}
              onAddSpouse={() => {
                setModal({ type: 'addRelative', data: { anchor: menu.member, type: 'spouse' } });
                closeMenu();
              }}
              onAddChild={() => {
                setModal({ type: 'addRelative', data: { anchor: menu.member, type: 'child' } });
                closeMenu();
              }}
              onClose={closeMenu}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FamilyTreePage;
